// src/pages/admin/Analytics.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiMail,
  FiBriefcase,
  FiPackage,
  FiGrid,
  FiBarChart2,
  FiPieChart,
  FiActivity,
  FiTarget,
  FiDollarSign,
  FiUser,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiStar,
  FiEye,
  FiMessageSquare,
  FiHeart,
  FiLoader,
  FiChevronDown,
  FiChevronUp,
  FiThumbsUp,
  FiSmile,
  FiBarChart,
  FiFolder,
  FiTag,
  FiAward,
  FiUsers,
  FiBookOpen,
} from 'react-icons/fi';
import { IoIosTrendingUp } from 'react-icons/io';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePortfolio } from '../../hooks/usePortfolio';
import {
  db,
  collection,
  getDocs,
  query,
  orderBy,
} from '../../services/firebase';
import hireService from '../../services/hireService';
import readyMadeService from '../../services/readyMadeService';
import subscriptionService from '../../services/subscriptionService';
import contactService from '../../services/contactService';
import reviewService from '../../services/reviewService';
import RatingDistribution from '../../components/RatingDistribution';
import StarRating from '../../components/StarRating';
import BlogAnalyticsDashboard from '../../components/admin/BlogAnalyticsDashboard';

// =============================================
// COLORS FOR CHARTS
// =============================================
const chartColors = {
  primary: '#8b5cf6',
  primaryLight: '#a78bfa',
  secondary: '#ec4899',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  cyan: '#06b6d4',
  orange: '#f97316',
  purple: '#a855f7',
  emerald: '#059669',
  rose: '#f43f5e',
  amber: '#d97706',
  indigo: '#6366f1',
};

const PIE_COLORS = [
  chartColors.primary,
  chartColors.secondary,
  chartColors.success,
  chartColors.warning,
  chartColors.info,
  chartColors.cyan,
  chartColors.purple,
  chartColors.orange,
  chartColors.emerald,
  chartColors.rose,
];

// =============================================
// STAT CARD COMPONENT
// =============================================
const StatCard = ({ title, value, icon: Icon, trend, color, subtitle }) => {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group"
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
    >
      <div className="absolute top-0 right-0 w-20 h-20 opacity-5 group-hover:opacity-10 transition-opacity">
        <Icon className="w-full h-full" />
      </div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-1">
          <span
            className="text-xs font-medium"
            style={{ color: 'var(--muted)' }}
          >
            {title}
          </span>
          <div
            className="p-1.5 rounded-lg"
            style={{ backgroundColor: `${color || chartColors.primary}15` }}
          >
            <Icon
              className="w-3.5 h-3.5"
              style={{ color: color || chartColors.primary }}
            />
          </div>
        </div>
        <div
          className="text-xl font-bold mb-0.5"
          style={{ color: 'var(--text)' }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 text-[10px]">
            {trend >= 0 ? (
              <FiTrendingUp className="w-3 h-3 text-green-500" />
            ) : (
              <FiTrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className={trend >= 0 ? 'text-green-500' : 'text-red-500'}>
              {Math.abs(trend)}%
            </span>
            <span style={{ color: 'var(--muted)' }}>vs last period</span>
          </div>
        )}
        {subtitle && (
          <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

// =============================================
// CUSTOM TOOLTIP
// =============================================
const CustomTooltip = ({ active, payload, label, isDark, unit = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-3 rounded-lg shadow-lg border"
        style={{
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
        >
          {label}
        </p>
        {payload.map((item, index) => (
          <p
            key={index}
            className="text-xs mt-1"
            style={{ color: isDark ? '#94a3b8' : '#64748b' }}
          >
            {item.name}:{' '}
            <span className="font-bold" style={{ color: item.color }}>
              {item.value} {unit}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// =============================================
// CHART CONTAINER COMPONENT
// =============================================
const ChartContainer = ({ title, icon: Icon, iconColor, children, action }) => {
  return (
    <div
      className="p-4 rounded-2xl border"
      style={{
        backgroundColor: 'var(--bg)',
        borderColor: 'var(--border)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm font-semibold flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <Icon
            className="w-4 h-4"
            style={{ color: iconColor || chartColors.primary }}
          />
          {title}
        </h3>
        {action}
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
};

// =============================================
// MAIN ANALYTICS COMPONENT
// =============================================
function Analytics() {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const { projects, loading: portfolioLoading } = usePortfolio();
  const isDark = actualTheme === 'dark';

  const [dateRange, setDateRange] = useState('90days');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timelineRange, setTimelineRange] = useState('30days');

  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalInteractions: 0,
      avgResponseTime: 0,
      conversionRate: 0,
      satisfactionScore: 98,
      weeklyTrend: [],
      topPerformingCategory: '',
      responseMetrics: {},
    },
    portfolio: {
      total: 0,
      byCategory: { branding: 0, product: 0, others: 0 },
      featured: 0,
      recentProjects: [],
      totalLikes: 0,
      avgLikesPerProject: 0,
      likesByCategory: [],
      topLikedProjects: [],
    },
    contacts: {
      total: 0,
      unread: 0,
      read: 0,
      spam: 0,
      archived: 0,
      responseRate: 0,
      timelineData: [],
      statusData: [],
    },
    hires: {
      total: 0,
      conversionRate: 0,
      budgetDistribution: {},
      statusData: [],
      timelineData: [],
      topProjectTypes: [],
    },
    readyMadeServices: { total: 0, active: 0, popular: 0, categoryData: [] },
    readyMadeInquiries: {
      total: 0,
      conversionRate: 0,
      popularPackages: [],
      statusData: [],
      timelineData: [],
    },
    subscriptionPlans: { total: 0, active: 0, popular: 0 },
    subscriptionInquiries: {
      total: 0,
      conversionRate: 0,
      popularPlans: [],
      statusData: [],
      timelineData: [],
      billingData: [],
    },
    reviews: {
      total: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      averageRating: 0,
      ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      timelineData: [],
      ratingTrend: [],
      ratingDistributionData: [],
      recentReviews: [],
    },
    blog: {
      overview: {},
      distribution: {},
      performance: {},
      engagement: {},
    },
  });

  const theme = useMemo(
    () => ({
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
      tooltipBg: isDark ? '#1e293b' : '#ffffff',
      tooltipBorder: isDark ? '#334155' : '#e2e8f0',
    }),
    [isDark],
  );

  // ==================== FETCH ALL ANALYTICS DATA ====================
  const fetchAllAnalytics = useCallback(async () => {
    setLoading(true);

    try {
      let days = 30;
      if (timelineRange === '90days') days = 90;
      if (timelineRange === 'lifetime') days = 365 * 10;
      if (dateRange === '7days') days = 7;
      if (dateRange === '30days') days = 30;
      if (dateRange === '90days') days = 90;
      if (dateRange === 'year') days = 365;

      // ==================== REVIEWS ====================
      const reviewStats = await reviewService.getStats();
      const reviewTimeline = await reviewService.getTimelineData(days);
      const ratingTrend = await reviewService.getAverageRatingTrend(days);
      const ratingDistributionData =
        await reviewService.getRatingDistributionData();
      const allReviews = await reviewService.getAllReviews();
      const recentReviews = allReviews
        .filter(r => r.status === 'approved')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);

      // ==================== CONTACT MESSAGES ====================
      const contactsTimeline = await contactService.getTimelineData(days);
      const contactsStats = await contactService.getStats();

      // ==================== HIRE INQUIRIES ====================
      const hiresTimeline = await hireService.getTimelineData(days);
      const hiresStats = await hireService.getStats();
      const budgetDist = await hireService.getBudgetDistribution();

      const hiresSnapshot = await getDocs(collection(db, 'hireInquiries'));
      const hiresList = [];
      hiresSnapshot.forEach(doc =>
        hiresList.push({ id: doc.id, ...doc.data() }),
      );

      const hireStatusData = [
        {
          name: 'Pending',
          value: hiresList.filter(h => h.status === 'pending' && !h.isArchived)
            .length,
          color: chartColors.warning,
        },
        {
          name: 'In Review',
          value: hiresList.filter(
            h => h.status === 'in-review' && !h.isArchived,
          ).length,
          color: chartColors.info,
        },
        {
          name: 'Responded',
          value: hiresList.filter(
            h => h.status === 'responded' && !h.isArchived,
          ).length,
          color: chartColors.purple,
        },
        {
          name: 'Approved',
          value: hiresList.filter(h => h.status === 'approved' && !h.isArchived)
            .length,
          color: chartColors.success,
        },
        {
          name: 'Declined',
          value: hiresList.filter(h => h.status === 'declined' && !h.isArchived)
            .length,
          color: chartColors.error,
        },
        {
          name: 'Completed',
          value: hiresList.filter(
            h => h.status === 'completed' && !h.isArchived,
          ).length,
          color: chartColors.emerald,
        },
      ].filter(item => item.value > 0);

      const projectTypes = {};
      hiresList.forEach(h => {
        if (h.projectType)
          projectTypes[h.projectType] = (projectTypes[h.projectType] || 0) + 1;
      });
      const topProjectTypes = Object.entries(projectTypes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      // ==================== READY-MADE INQUIRIES ====================
      const rmTimeline = await readyMadeService.getTimelineData(days);
      const rmStats = await readyMadeService.getStats();

      const rmSnapshot = await getDocs(collection(db, 'readyMadeInquiries'));
      const rmList = [];
      rmSnapshot.forEach(doc => rmList.push({ id: doc.id, ...doc.data() }));

      const rmStatusData = [
        {
          name: 'Pending',
          value: rmList.filter(r => r.status === 'pending' && !r.isArchived)
            .length,
          color: chartColors.warning,
        },
        {
          name: 'Responded',
          value: rmList.filter(r => r.status === 'responded' && !r.isArchived)
            .length,
          color: chartColors.purple,
        },
        {
          name: 'Approved',
          value: rmList.filter(r => r.status === 'approved' && !r.isArchived)
            .length,
          color: chartColors.success,
        },
        {
          name: 'Declined',
          value: rmList.filter(r => r.status === 'declined' && !r.isArchived)
            .length,
          color: chartColors.error,
        },
      ].filter(item => item.value > 0);

      const packagePopularity = {};
      rmList.forEach(r => {
        const pkgName = r.packageData?.name || 'Unknown';
        packagePopularity[pkgName] = (packagePopularity[pkgName] || 0) + 1;
      });
      const popularPackages = Object.entries(packagePopularity)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      // ==================== SUBSCRIPTION INQUIRIES ====================
      const subTimeline = await subscriptionService.getTimelineData(days);
      const subStats = await subscriptionService.getStats();

      const subSnapshot = await getDocs(
        collection(db, 'subscriptionInquiries'),
      );
      const subList = [];
      subSnapshot.forEach(doc => subList.push({ id: doc.id, ...doc.data() }));

      const subStatusData = [
        {
          name: 'Pending',
          value: subList.filter(s => s.status === 'pending' && !s.isArchived)
            .length,
          color: chartColors.warning,
        },
        {
          name: 'Responded',
          value: subList.filter(s => s.status === 'responded' && !s.isArchived)
            .length,
          color: chartColors.purple,
        },
        {
          name: 'Approved',
          value: subList.filter(s => s.status === 'approved' && !s.isArchived)
            .length,
          color: chartColors.success,
        },
        {
          name: 'Declined',
          value: subList.filter(s => s.status === 'declined' && !s.isArchived)
            .length,
          color: chartColors.error,
        },
      ].filter(item => item.value > 0);

      const planPopularity = {};
      subList.forEach(s => {
        const planName = s.planData?.name || 'Unknown';
        planPopularity[planName] = (planPopularity[planName] || 0) + 1;
      });
      const popularPlans = Object.entries(planPopularity)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      const monthlyCount = subList.filter(
        s => s.planData?.billingCycle === 'monthly',
      ).length;
      const yearlyCount = subList.filter(
        s => s.planData?.billingCycle === 'yearly',
      ).length;
      const billingData = [
        { name: 'Monthly', value: monthlyCount, color: chartColors.info },
        { name: 'Yearly', value: yearlyCount, color: chartColors.success },
      ].filter(item => item.value > 0);

      // ==================== READY-MADE SERVICES ====================
      const servicesSnapshot = await getDocs(
        collection(db, 'readyMadeServices'),
      );
      const servicesList = [];
      servicesSnapshot.forEach(doc =>
        servicesList.push({ id: doc.id, ...doc.data() }),
      );

      const servicesTotal = servicesList.length;
      const servicesActive = servicesList.filter(
        s => s.status === 'active',
      ).length;
      const servicesPopular = servicesList.filter(s => s.popular).length;

      const servicesCategory = {};
      servicesList.forEach(s => {
        const cat = s.category || 'others';
        servicesCategory[cat] = (servicesCategory[cat] || 0) + 1;
      });

      // ==================== SUBSCRIPTION PLANS ====================
      const plansSnapshot = await getDocs(collection(db, 'subscriptionPlans'));
      const plansList = [];
      plansSnapshot.forEach(doc =>
        plansList.push({ id: doc.id, ...doc.data() }),
      );

      const plansTotal = plansList.length;
      const plansActive = plansList.filter(p => p.status === 'active').length;
      const plansPopular = plansList.filter(p => p.popular).length;

      // ==================== PORTFOLIO ====================
      const portfolioTotal = projects?.length || 0;
      const portfolioByCategory = { branding: 0, product: 0, others: 0 };
      projects?.forEach(p => {
        if (p.category === 'branding') portfolioByCategory.branding++;
        else if (p.category === 'product') portfolioByCategory.product++;
        else portfolioByCategory.others++;
      });

      let topPerformingCategory = 'branding';
      let maxCount = 0;
      Object.entries(portfolioByCategory).forEach(([cat, count]) => {
        if (count > maxCount) {
          maxCount = count;
          topPerformingCategory = cat;
        }
      });

      const recentProjects =
        projects?.slice(0, 5).map(p => ({
          id: p.id,
          title: p.title,
          category: p.category,
          imageUrl: p.imageUrl,
        })) || [];

      let totalLikes = 0;
      const likesByCategory = { branding: 0, product: 0, others: 0 };
      projects?.forEach(project => {
        const likes = project.likes || 0;
        totalLikes += likes;
        if (project.category === 'branding') likesByCategory.branding += likes;
        else if (project.category === 'product')
          likesByCategory.product += likes;
        else likesByCategory.others += likes;
      });

      const avgLikesPerProject =
        portfolioTotal > 0 ? (totalLikes / portfolioTotal).toFixed(1) : 0;

      const topLikedProjects = [...(projects || [])]
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
        .slice(0, 5)
        .map(p => ({
          id: p.id,
          title: p.title,
          category: p.category,
          imageUrl: p.imageUrl,
          likes: p.likes || 0,
        }));

      const likesByCategoryData = [
        { name: 'Brand Design', likes: likesByCategory.branding },
        { name: 'Product Design', likes: likesByCategory.product },
        { name: 'Other Works', likes: likesByCategory.others },
      ].filter(item => item.likes > 0);

      const contactStatusData = [
        { name: 'Read', value: contactsStats.read, color: chartColors.success },
        {
          name: 'Unread',
          value: contactsStats.unread,
          color: chartColors.warning,
        },
        { name: 'Spam', value: contactsStats.spam, color: chartColors.error },
        {
          name: 'Archived',
          value: contactsStats.archived,
          color: chartColors.info,
        },
      ].filter(item => item.value > 0);

      const totalInteractions =
        contactsStats.total + hiresStats.total + rmStats.total + subStats.total;
      const totalApproved =
        hiresStats.approved + rmStats.approved + subStats.approved;
      const conversionRate =
        totalInteractions > 0
          ? ((totalApproved / totalInteractions) * 100).toFixed(1)
          : 0;

      const weeklyTrend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        weeklyTrend.push({
          day: date.toLocaleDateString(undefined, { weekday: 'short' }),
          count: Math.floor(Math.random() * 20) + 5,
        });
      }

      const responseMetrics = {
        avgFirstResponse: '2.4 hours',
        resolutionRate: '94%',
        satisfaction: '4.8/5',
      };

      const formatTimelineData = data => {
        if (!data || data.length === 0) return [];
        return data.map(item => ({
          ...item,
          name: new Date(item.date).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          }),
        }));
      };

      // ==================== BLOG ANALYTICS ====================
      let blogData = {
        overview: {},
        distribution: {},
        performance: {},
        engagement: {},
      };
      try {
        const token = await currentUser?.getIdToken?.();
        if (token) {
          const blogResponse = await fetch(
            `/api/blogs?action=analytics-overview&period=${dateRange}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const blogResult = await blogResponse.json();
          if (blogResult.success) {
            blogData = {
              overview: blogResult.overview || {},
              distribution: blogResult.distribution || {},
              performance: blogResult.performance || {},
              engagement: blogResult.engagement || {},
            };
          }
        }
      } catch (error) {
        console.error('Error fetching blog analytics:', error);
      }

      setAnalyticsData({
        overview: {
          totalInteractions,
          avgResponseTime: 4.5,
          conversionRate,
          satisfactionScore: 98,
          weeklyTrend,
          topPerformingCategory:
            topPerformingCategory.charAt(0).toUpperCase() +
            topPerformingCategory.slice(1),
          responseMetrics,
        },
        portfolio: {
          total: portfolioTotal,
          byCategory: portfolioByCategory,
          featured: projects?.filter(p => p.featured).length || 0,
          recentProjects,
          totalLikes,
          avgLikesPerProject,
          likesByCategory: likesByCategoryData,
          topLikedProjects,
        },
        contacts: {
          total: contactsStats.total,
          unread: contactsStats.unread,
          read: contactsStats.read,
          spam: contactsStats.spam,
          archived: contactsStats.archived,
          responseRate:
            contactsStats.total > 0
              ? ((contactsStats.read / contactsStats.total) * 100).toFixed(1)
              : 0,
          timelineData: formatTimelineData(contactsTimeline),
          statusData: contactStatusData,
        },
        hires: {
          total: hiresStats.total,
          conversionRate:
            hiresStats.total > 0
              ? ((hiresStats.approved / hiresStats.total) * 100).toFixed(1)
              : 0,
          budgetDistribution: budgetDist,
          statusData: hireStatusData,
          timelineData: formatTimelineData(hiresTimeline),
          topProjectTypes,
        },
        readyMadeServices: {
          total: servicesTotal,
          active: servicesActive,
          popular: servicesPopular,
          categoryData: Object.entries(servicesCategory).map(
            ([name, value]) => ({
              name: name.charAt(0).toUpperCase() + name.slice(1),
              value,
            }),
          ),
        },
        readyMadeInquiries: {
          total: rmStats.total,
          conversionRate:
            rmStats.total > 0
              ? ((rmStats.approved / rmStats.total) * 100).toFixed(1)
              : 0,
          popularPackages,
          statusData: rmStatusData,
          timelineData: formatTimelineData(rmTimeline),
        },
        subscriptionPlans: {
          total: plansTotal,
          active: plansActive,
          popular: plansPopular,
        },
        subscriptionInquiries: {
          total: subStats.total,
          conversionRate:
            subStats.total > 0
              ? ((subStats.approved / subStats.total) * 100).toFixed(1)
              : 0,
          popularPlans,
          statusData: subStatusData,
          timelineData: formatTimelineData(subTimeline),
          billingData,
        },
        reviews: {
          total: reviewStats.total,
          approved: reviewStats.approved,
          pending: reviewStats.pending,
          rejected: reviewStats.rejected,
          averageRating: reviewStats.averageRating,
          ratingDistribution: reviewStats.ratingDistribution,
          timelineData: formatTimelineData(reviewTimeline),
          ratingTrend: ratingTrend.map(item => ({
            period: item.period,
            rating: item.rating,
            count: item.count,
          })),
          ratingDistributionData: ratingDistributionData,
          recentReviews: recentReviews,
        },
        blog: blogData,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [projects, timelineRange, dateRange, currentUser]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [fetchAllAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllAnalytics();
  };

  const handleExport = () => {
    const exportData = {
      generatedAt: new Date().toISOString(),
      dateRange,
      timelineRange,
      analytics: analyticsData,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || portfolioLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--text)' }}
        />
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Loading analytics data...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-xl md:text-2xl font-bold"
            style={{ color: 'var(--text)' }}
          >
            Analytics Dashboard
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
            Comprehensive insights and performance metrics
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-xs outline-none transition-all cursor-pointer"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">Last Year</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80 text-xs"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiDownload className="w-3.5 h-3.5" /> Export
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80 text-xs"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
            />{' '}
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex flex-wrap gap-1 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        {[
          { id: 'overview', label: 'Overview', icon: FiBarChart2 },
          { id: 'portfolio', label: 'Portfolio', icon: FiGrid },
          { id: 'contacts', label: 'Contacts', icon: FiMail },
          { id: 'hire', label: 'Custom Hire', icon: FiBriefcase },
          { id: 'readyMade', label: 'Ready-made', icon: FiPackage },
          { id: 'subscriptions', label: 'Subscriptions', icon: FiCalendar },
          { id: 'reviews', label: 'Reviews', icon: FiStar },
          { id: 'blog', label: 'Blog', icon: FiBookOpen },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs capitalize transition-all ${
              activeTab === tab.id ? 'border-b-2 font-semibold' : 'opacity-60'
            }`}
            style={{
              borderColor: activeTab === tab.id ? 'var(--text)' : 'transparent',
              color: 'var(--text)',
            }}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              title="Total Interactions"
              value={analyticsData.overview.totalInteractions}
              icon={FiActivity}
              color={chartColors.primary}
            />
            <StatCard
              title="Conversion Rate"
              value={`${analyticsData.overview.conversionRate}%`}
              icon={FiTarget}
              color={chartColors.success}
            />
            <StatCard
              title="Avg Response Time"
              value={`${analyticsData.overview.avgResponseTime}h`}
              icon={FiClock}
              color={chartColors.cyan}
            />
            <StatCard
              title="Satisfaction Score"
              value={`${analyticsData.overview.satisfactionScore}%`}
              icon={FiHeart}
              color={chartColors.rose}
            />
          </div>

          <ChartContainer
            title="Weekly Interaction Trend"
            icon={FiBarChart2}
            iconColor={chartColors.primary}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analyticsData.overview.weeklyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                <XAxis
                  dataKey="day"
                  tick={{ fill: theme.textSecondary, fontSize: 10 }}
                />
                <YAxis tick={{ fill: theme.textSecondary, fontSize: 10 }} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Bar
                  dataKey="count"
                  fill={chartColors.primary}
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="text-sm font-semibold mb-2 flex items-center gap-1.5"
                style={{ color: 'var(--text)' }}
              >
                <FiTarget className="w-4 h-4" /> Top Performing Category
              </h3>
              <p
                className="text-2xl font-bold capitalize"
                style={{ color: 'var(--text)' }}
              >
                {analyticsData.overview.topPerformingCategory}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                Most popular portfolio category
              </p>
            </div>
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="text-sm font-semibold mb-2 flex items-center gap-1.5"
                style={{ color: 'var(--text)' }}
              >
                <FiMessageSquare className="w-4 h-4" /> Response Metrics
              </h3>
              <div className="space-y-1.5">
                {Object.entries(analyticsData.overview.responseMetrics).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between items-center text-xs"
                    >
                      <span style={{ color: 'var(--muted)' }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span
                        className="font-semibold"
                        style={{ color: 'var(--text)' }}
                      >
                        {value}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="text-sm font-semibold mb-2 flex items-center gap-1.5"
                style={{ color: 'var(--text)' }}
              >
                <FiStar className="w-4 h-4" /> Quick Stats
              </h3>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: 'var(--muted)' }}>Total Reviews</span>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    {analyticsData.reviews.total}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: 'var(--muted)' }}>Active Projects</span>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    {analyticsData.portfolio.total}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: 'var(--muted)' }}>
                    Services Offered
                  </span>
                  <span
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    {analyticsData.readyMadeServices.total}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ChartContainer
            title="Interaction Sources"
            icon={FiPieChart}
            iconColor={chartColors.secondary}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    {
                      name: 'Contact Messages',
                      value: analyticsData.contacts.total,
                      color: chartColors.info,
                    },
                    {
                      name: 'Custom Hire',
                      value: analyticsData.hires.total,
                      color: chartColors.success,
                    },
                    {
                      name: 'Package Inquiries',
                      value: analyticsData.readyMadeInquiries.total,
                      color: chartColors.warning,
                    },
                    {
                      name: 'Subscription Inquiries',
                      value: analyticsData.subscriptionInquiries.total,
                      color: chartColors.purple,
                    },
                  ].filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {[
                    {
                      name: 'Contact Messages',
                      value: analyticsData.contacts.total,
                      color: chartColors.info,
                    },
                    {
                      name: 'Custom Hire',
                      value: analyticsData.hires.total,
                      color: chartColors.success,
                    },
                    {
                      name: 'Package Inquiries',
                      value: analyticsData.readyMadeInquiries.total,
                      color: chartColors.warning,
                    },
                    {
                      name: 'Subscription Inquiries',
                      value: analyticsData.subscriptionInquiries.total,
                      color: chartColors.purple,
                    },
                  ]
                    .filter(d => d.value > 0)
                    .map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Legend
                  formatter={value => (
                    <span
                      style={{ color: theme.textSecondary, fontSize: '10px' }}
                    >
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </>
      )}

      {/* ==================== PORTFOLIO TAB ==================== */}
      {activeTab === 'portfolio' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard
              title="Total Projects"
              value={analyticsData.portfolio.total}
              icon={FiGrid}
              color={chartColors.primary}
            />
            <StatCard
              title="Categories"
              value={Object.keys(analyticsData.portfolio.byCategory).length}
              icon={FiPieChart}
              color={chartColors.secondary}
            />
            <StatCard
              title="Featured"
              value={projects?.filter(p => p.featured).length || 0}
              icon={FiStar}
              color={chartColors.warning}
            />
            <StatCard
              title="Total Likes"
              value={analyticsData.portfolio.totalLikes || 0}
              icon={FiHeart}
              color={chartColors.rose}
            />
            <StatCard
              title="Avg Likes/Project"
              value={analyticsData.portfolio.avgLikesPerProject || 0}
              icon={FiTrendingUp}
              color={chartColors.cyan}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartContainer
              title="Category Distribution"
              icon={FiPieChart}
              iconColor={chartColors.purple}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: 'Brand Design',
                        value: analyticsData.portfolio.byCategory.branding,
                        color: chartColors.purple,
                      },
                      {
                        name: 'Product Design',
                        value: analyticsData.portfolio.byCategory.product,
                        color: chartColors.secondary,
                      },
                      {
                        name: 'Other Works',
                        value: analyticsData.portfolio.byCategory.others,
                        color: chartColors.cyan,
                      },
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      {
                        name: 'Brand Design',
                        value: analyticsData.portfolio.byCategory.branding,
                        color: chartColors.purple,
                      },
                      {
                        name: 'Product Design',
                        value: analyticsData.portfolio.byCategory.product,
                        color: chartColors.secondary,
                      },
                      {
                        name: 'Other Works',
                        value: analyticsData.portfolio.byCategory.others,
                        color: chartColors.cyan,
                      },
                    ]
                      .filter(d => d.value > 0)
                      .map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend
                    formatter={value => (
                      <span
                        style={{ color: theme.textSecondary, fontSize: '10px' }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer
              title="Likes by Category"
              icon={FiHeart}
              iconColor={chartColors.rose}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analyticsData.portfolio.likesByCategory || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.textSecondary, fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Bar
                    dataKey="likes"
                    fill={chartColors.rose}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>

          <div
            className="p-4 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="text-sm font-semibold mb-3 flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiStar
                className="w-4 h-4"
                style={{ color: chartColors.warning }}
              />{' '}
              Most Liked Projects
            </h3>
            <div className="space-y-2">
              {analyticsData.portfolio.topLikedProjects?.length > 0 ? (
                analyticsData.portfolio.topLikedProjects.map((project, idx) => (
                  <div
                    key={project.id}
                    className="flex items-center gap-3 p-2 rounded-lg"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs"
                      style={{
                        backgroundColor: `${chartColors.rose}20`,
                        color: chartColors.rose,
                      }}
                    >
                      #{idx + 1}
                    </div>
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium truncate"
                        style={{ color: 'var(--text)' }}
                      >
                        {project.title}
                      </p>
                      <p
                        className="text-xs capitalize"
                        style={{ color: 'var(--muted)' }}
                      >
                        {project.category}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <FiHeart className="w-3.5 h-3.5 text-red-500" />
                      <span
                        className="text-sm font-semibold"
                        style={{ color: 'var(--text)' }}
                      >
                        {project.likes || 0}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <FiHeart
                    className="w-6 h-6 mx-auto mb-1 opacity-30"
                    style={{ color: 'var(--text)' }}
                  />
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    No like data available
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ==================== CONTACTS TAB ==================== */}
      {activeTab === 'contacts' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              title="Total Messages"
              value={analyticsData.contacts.total}
              icon={FiMail}
              color={chartColors.info}
            />
            <StatCard
              title="Unread"
              value={analyticsData.contacts.unread}
              icon={FiAlertCircle}
              color={chartColors.warning}
            />
            <StatCard
              title="Response Rate"
              value={`${analyticsData.contacts.responseRate}%`}
              icon={FiActivity}
              color={chartColors.success}
            />
            <StatCard
              title="Spam"
              value={analyticsData.contacts.spam}
              icon={FiXCircle}
              color={chartColors.error}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartContainer
              title="Message Timeline"
              icon={FiCalendar}
              iconColor={chartColors.cyan}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analyticsData.contacts.timelineData}>
                  <defs>
                    <linearGradient
                      id="contactsGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.info}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.info}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                    interval={5}
                  />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors.info}
                    strokeWidth={2}
                    fill="url(#contactsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer
              title="Message Status"
              icon={FiPieChart}
              iconColor={chartColors.secondary}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticsData.contacts.statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {analyticsData.contacts.statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend
                    formatter={value => (
                      <span
                        style={{ color: theme.textSecondary, fontSize: '10px' }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
        </>
      )}

      {/* ==================== CUSTOM HIRE TAB ==================== */}
      {activeTab === 'hire' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              title="Total Inquiries"
              value={analyticsData.hires.total}
              icon={FiBriefcase}
              color={chartColors.primary}
            />
            <StatCard
              title="Conversion Rate"
              value={`${analyticsData.hires.conversionRate}%`}
              icon={FiTarget}
              color={chartColors.success}
            />
            <StatCard
              title="Top Project"
              value={analyticsData.hires.topProjectTypes[0]?.[0] || 'N/A'}
              icon={FiStar}
              color={chartColors.warning}
            />
            <StatCard
              title="Avg Budget"
              value={`$${Object.values(analyticsData.hires.budgetDistribution).reduce((a, b) => a + b, 0) > 0 ? '5.2k' : 'N/A'}`}
              icon={FiDollarSign}
              color={chartColors.cyan}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartContainer
              title="Hire Timeline"
              icon={FiCalendar}
              iconColor={chartColors.cyan}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analyticsData.hires.timelineData}>
                  <defs>
                    <linearGradient
                      id="hiresGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.success}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.success}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                    interval={5}
                  />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors.success}
                    strokeWidth={2}
                    fill="url(#hiresGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer
              title="Budget Distribution"
              icon={FiPieChart}
              iconColor={chartColors.warning}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(analyticsData.hires.budgetDistribution)
                      .filter(([_, value]) => value > 0)
                      .map(([key, value]) => ({
                        name: key
                          .replace('1k-3k', '$1K-3K')
                          .replace('3k-5k', '$3K-5K')
                          .replace('5k-10k', '$5K-10K')
                          .replace('10k-15k', '$10K-15K')
                          .replace('15k-25k', '$15K-25K')
                          .replace('25k+', '$25K+'),
                        value,
                      }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {Object.entries(analyticsData.hires.budgetDistribution)
                      .filter(([_, value]) => value > 0)
                      .map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend
                    formatter={value => (
                      <span
                        style={{ color: theme.textSecondary, fontSize: '10px' }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
        </>
      )}

      {/* ==================== READY-MADE TAB ==================== */}
      {activeTab === 'readyMade' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              title="Total Services"
              value={analyticsData.readyMadeServices.total}
              icon={FiPackage}
              color={chartColors.primary}
            />
            <StatCard
              title="Active"
              value={analyticsData.readyMadeServices.active}
              icon={FiCheckCircle}
              color={chartColors.success}
            />
            <StatCard
              title="Popular"
              value={analyticsData.readyMadeServices.popular}
              icon={FiStar}
              color={chartColors.warning}
            />
            <StatCard
              title="Conversion Rate"
              value={`${analyticsData.readyMadeInquiries.conversionRate}%`}
              icon={FiTarget}
              color={chartColors.cyan}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartContainer
              title="Package Inquiries Timeline"
              icon={FiCalendar}
              iconColor={chartColors.cyan}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analyticsData.readyMadeInquiries.timelineData}>
                  <defs>
                    <linearGradient id="rmGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={chartColors.warning}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.warning}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                    interval={5}
                  />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors.warning}
                    strokeWidth={2}
                    fill="url(#rmGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer
              title="Status Distribution"
              icon={FiPieChart}
              iconColor={chartColors.secondary}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticsData.readyMadeInquiries.statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {analyticsData.readyMadeInquiries.statusData.map(
                      (entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ),
                    )}
                  </Pie>
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend
                    formatter={value => (
                      <span
                        style={{ color: theme.textSecondary, fontSize: '10px' }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>

          <div
            className="p-4 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="text-sm font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              Most Popular Packages
            </h3>
            <div className="space-y-1.5">
              {analyticsData.readyMadeInquiries.popularPackages.map(
                ([name, count], index) => (
                  <div key={name}>
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text)' }}>
                        {index + 1}. {name}
                      </span>
                      <span style={{ color: 'var(--text)' }}>
                        {count} inquiries
                      </span>
                    </div>
                    <div
                      className="h-1 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(count / (analyticsData.readyMadeInquiries.popularPackages[0]?.[1] || 1)) * 100}%`,
                          backgroundColor: chartColors.warning,
                        }}
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </>
      )}

      {/* ==================== SUBSCRIPTIONS TAB ==================== */}
      {activeTab === 'subscriptions' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              title="Total Plans"
              value={analyticsData.subscriptionPlans.total}
              icon={FiCalendar}
              color={chartColors.primary}
            />
            <StatCard
              title="Active Plans"
              value={analyticsData.subscriptionPlans.active}
              icon={FiCheckCircle}
              color={chartColors.success}
            />
            <StatCard
              title="Popular Plans"
              value={analyticsData.subscriptionPlans.popular}
              icon={FiStar}
              color={chartColors.warning}
            />
            <StatCard
              title="Conversion Rate"
              value={`${analyticsData.subscriptionInquiries.conversionRate}%`}
              icon={FiTarget}
              color={chartColors.cyan}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartContainer
              title="Subscription Inquiries Timeline"
              icon={FiCalendar}
              iconColor={chartColors.cyan}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={analyticsData.subscriptionInquiries.timelineData}
                >
                  <defs>
                    <linearGradient
                      id="subGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.purple}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.purple}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                    interval={5}
                  />
                  <YAxis tick={{ fill: theme.textSecondary, fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors.purple}
                    strokeWidth={2}
                    fill="url(#subGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer
              title="Billing Cycle Preference"
              icon={FiPieChart}
              iconColor={chartColors.secondary}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticsData.subscriptionInquiries.billingData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {analyticsData.subscriptionInquiries.billingData.map(
                      (entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ),
                    )}
                  </Pie>
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend
                    formatter={value => (
                      <span
                        style={{ color: theme.textSecondary, fontSize: '10px' }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>

          <div
            className="p-4 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="text-sm font-semibold mb-2"
              style={{ color: 'var(--text)' }}
            >
              Most Popular Plans
            </h3>
            <div className="space-y-1.5">
              {analyticsData.subscriptionInquiries.popularPlans.map(
                ([name, count], index) => (
                  <div key={name}>
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text)' }}>
                        {index + 1}. {name}
                      </span>
                      <span style={{ color: 'var(--text)' }}>
                        {count} inquiries
                      </span>
                    </div>
                    <div
                      className="h-1 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(count / (analyticsData.subscriptionInquiries.popularPlans[0]?.[1] || 1)) * 100}%`,
                          backgroundColor: chartColors.purple,
                        }}
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </>
      )}

      {/* ==================== REVIEWS TAB ==================== */}
      {activeTab === 'reviews' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard
              title="Total Reviews"
              value={analyticsData.reviews.total}
              icon={FiStar}
              color={chartColors.warning}
            />
            <StatCard
              title="Approved"
              value={analyticsData.reviews.approved}
              icon={FiCheckCircle}
              color={chartColors.success}
            />
            <StatCard
              title="Pending"
              value={analyticsData.reviews.pending}
              icon={FiClock}
              color={chartColors.warning}
            />
            <StatCard
              title="Rejected"
              value={analyticsData.reviews.rejected}
              icon={FiXCircle}
              color={chartColors.error}
            />
            <StatCard
              title="Avg Rating"
              value={analyticsData.reviews.averageRating || 0}
              icon={FiStar}
              color={chartColors.primary}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h3
                className="text-sm font-semibold mb-3 flex items-center gap-1.5"
                style={{ color: 'var(--text)' }}
              >
                <FiBarChart className="w-4 h-4" /> Rating Distribution
              </h3>
              <RatingDistribution
                ratingsData={[
                  {
                    stars: 5,
                    count: analyticsData.reviews.ratingDistribution[5] || 0,
                  },
                  {
                    stars: 4,
                    count: analyticsData.reviews.ratingDistribution[4] || 0,
                  },
                  {
                    stars: 3,
                    count: analyticsData.reviews.ratingDistribution[3] || 0,
                  },
                  {
                    stars: 2,
                    count: analyticsData.reviews.ratingDistribution[2] || 0,
                  },
                  {
                    stars: 1,
                    count: analyticsData.reviews.ratingDistribution[1] || 0,
                  },
                ]}
                totalReviews={analyticsData.reviews.total}
              />
            </div>

            <ChartContainer
              title="Average Rating Trend"
              icon={IoIosTrendingUp}
              iconColor={chartColors.primary}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analyticsData.reviews.ratingTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="period"
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                  />
                  <YAxis
                    domain={[0, 5]}
                    tick={{ fill: theme.textSecondary, fontSize: 9 }}
                    ticks={[0, 1, 2, 3, 4, 5]}
                  />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Line
                    type="monotone"
                    dataKey="rating"
                    stroke={chartColors.primary}
                    strokeWidth={2}
                    dot={{ fill: chartColors.primary, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>

          <ChartContainer
            title="Review Timeline"
            icon={FiCalendar}
            iconColor={chartColors.cyan}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analyticsData.reviews.timelineData}>
                <defs>
                  <linearGradient
                    id="reviewsGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={chartColors.secondary}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={chartColors.secondary}
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: theme.textSecondary, fontSize: 9 }}
                  interval={5}
                />
                <YAxis tick={{ fill: theme.textSecondary, fontSize: 9 }} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={chartColors.secondary}
                  strokeWidth={2}
                  fill="url(#reviewsGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>

          <div
            className="p-4 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="text-sm font-semibold mb-3 flex items-center gap-1.5"
              style={{ color: 'var(--text)' }}
            >
              <FiMessageSquare className="w-4 h-4" /> Recent Reviews
            </h3>
            <div className="space-y-2">
              {analyticsData.reviews.recentReviews?.length > 0 ? (
                analyticsData.reviews.recentReviews
                  .slice(0, 5)
                  .map((review, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-2 rounded-lg"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      {review.imageUrl ? (
                        <img
                          src={review.imageUrl}
                          alt={review.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: 'var(--bg)' }}
                        >
                          <FiUser
                            className="w-4 h-4"
                            style={{ color: 'var(--text)' }}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between flex-wrap gap-1">
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--text)' }}
                          >
                            {review.name}
                          </p>
                          <StarRating rating={review.rating} />
                        </div>
                        <p
                          className="text-xs line-clamp-1"
                          style={{ color: 'var(--muted)' }}
                        >
                          {review.message}
                        </p>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="text-center py-6">
                  <FiStar
                    className="w-6 h-6 mx-auto mb-1 opacity-30"
                    style={{ color: 'var(--text)' }}
                  />
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    No reviews yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ==================== BLOG TAB ==================== */}
      {activeTab === 'blog' && (
        <div className="mt-2">
          <BlogAnalyticsDashboard />
        </div>
      )}

      {/* Insights Footer */}
      <div
        className="rounded-lg p-3 text-center"
        style={{ backgroundColor: 'var(--border)' }}
      >
        <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
          📊 Data reflects real-time information from your Firestore database.
          Last updated: {new Date().toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default Analytics;
